/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/require-default-props */
import '../Utils/css';
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './Input.module.scss';

/**
 * Input Component
 *
 * A versatile and styled input component with optional label support. This component
 * handles internal state for its value and adjusts label behavior based on focus or input.
 */
export default function Input({
  autoFocus = false,
  button = null,
  help = null,
  errorMessage: customErrorMessage = (messages, _ref) => messages,
  setInputRef,
  type = 'text',
  label,
  name,
  className,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  value: externalValue,
  isValid: extraValidation = null,
  showValidation = true,
  errors: showErrors = true,
  ...childProps
}) {
  const [interactedWith, setInteractedWith] = useState(false);
  const [internalValue, setInternalValue] = useState(() => externalValue ?? '');
  const [isActive, setIsActive] = useState(!!externalValue || !!placeholder || autoFocus);
  const [errors, setErrors] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    setInputRef?.(inputRef.current);
  }, [setInputRef]);

  useEffect(() => {
    if (externalValue !== undefined) {
      setInternalValue((prev) => (externalValue !== prev ? externalValue : prev));
    }

    const newIsActive = !!externalValue || !!placeholder || autoFocus;
    if (newIsActive !== isActive) {
      setIsActive(newIsActive);
    }
  }, [externalValue, placeholder, autoFocus]);

  useEffect(() => {
    if (isActive && !interactedWith && internalValue !== '') {
      setInteractedWith(true);
    }
  }, [isActive, interactedWith, internalValue]);

  const errorMessages = () => {
    const messages = [];
    if (inputRef.current?.validity.valueMissing) {
      messages.push({
        message: `Please enter your ${label.toLowerCase()}`,
        type: 'valueMissing',
      });
    }

    if (inputRef.current?.value && inputRef.current?.validity.typeMismatch) {
      messages.push({
        message: `Please enter a ${label.toLowerCase()}`,
        type: 'typeMismatch',
      });
    }

    if (inputRef.current?.value
      && inputRef.current?.validity.patternMismatch
      && childProps?.pattern == null
    ) {
      messages.push({
        message: `Please enter a valid ${label.toLowerCase()}`,
        type: 'patternMismatch',
      });
    }

    if (inputRef.current?.value && inputRef.current?.validity.tooShort) {
      messages.push({
        message: `${label} Must be at least ${childProps?.minLength} characters in length`,
        type: 'tooShort',
      });
    }

    return customErrorMessage(messages, inputRef.current);
  };

  const handleChange = (e) => {
    const { value } = e.target;
    setInternalValue(value);
    onChange?.(e);
    setErrors([]);
  };

  const handleFocus = (e) => {
    setIsActive(true);
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    if (!internalValue) setIsActive(false);
    if (!interactedWith) setInteractedWith(true);
    onBlur?.(e);
    setErrors(errorMessages());
  };

  const isValid = () => {
    const inputValidity = inputRef.current?.validity?.valid ?? true;
    const customValidity = extraValidation ? extraValidation(inputRef.current) : true;
    return inputValidity && customValidity;
  };

  const validationClass = () => {
    if (!showValidation || !interactedWith) return '';
    return isValid() ? styles['--valid'] : styles['--invalid'];
  };

  return (
    <div className={[styles.input, className].css()}>
      {label && (
      <label htmlFor={name} className={[isActive ? styles['--active'] : null].css()}>{label}</label>
      )}
      <input
        className={validationClass()}
        ref={inputRef}
        type={type}
        name={name}
        value={internalValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        {...childProps}
      />
      {button}
      {help && <div className={styles.input__help}>{help}</div>}
      {showErrors && errors && errors.length > 0 && (
      <ul className={styles.input__errors}>
        {errors.map((error, index) => (
          <li key={index}>{error.message}</li>
        ))}
      </ul>
      )}
    </div>
  );
}

Input.styles = {
  button: styles.input__button,
};

Input.propTypes = {
  /**
   * Automatically focuses the input field when the component mounts.
   * Default: false.
   */
  autoFocus: PropTypes.bool,

  /**
   * Additional CSS class names for custom styling.
   * Applied to the root `div` container of the input component.
   */
  className: PropTypes.string,

  /**
   * Optional React node to render inside the input container (usually a button).
   * This can be used for things like clear buttons or input-affixed actions.
   */
  button: PropTypes.node,

  /**
   * The label text displayed above the input field.
   * Provides context for the input’s purpose.
   */
  label: PropTypes.string,

  /**
   * The unique name/ID for the input field.
   * This is required for accessibility and form handling.
   */
  name: PropTypes.string.isRequired,

  /**
   * Callback function triggered when the input loses focus.
   * Receives the native `blur` event as an argument.
   */
  onBlur: PropTypes.func,

  /**
   * Callback function triggered when the input value changes.
   * Receives the native `change` event as an argument.
   */
  onChange: PropTypes.func,

  /**
   * Callback function triggered when the input gains focus.
   * Receives the native `focus` event as an argument.
   */
  onFocus: PropTypes.func,

  /**
   * Placeholder text displayed inside the input field when no value is present.
   * Typically provides a hint about the expected input format.
   */
  placeholder: PropTypes.string,

  /**
   * The input type (e.g., "text", "password", "email").
   * Determines the behavior and appearance of the input.
   * Default: "text".
   */
  type: PropTypes.string,

  /**
   * The externally controlled value of the input field.
   * Used in controlled components to bind the input value to a state or prop.
   */
  value: PropTypes.string,

  /**
   * Determines whether validation styles (valid/invalid) are applied to the input field.
   * Default: true.
   */
  showValidation: PropTypes.bool,

  /**
   * A custom validation function that takes the input element as an argument.
   * Should return a boolean indicating whether the input is valid.
   */
  isValid: PropTypes.func,

  /**
   * Additional helper text displayed below the input field.
   * Useful for providing extra guidance to the user.
   */
  help: PropTypes.string,

  /**
   * Function to customize error messages displayed below the input field.
   * Receives an array of default error messages and the input element.
   * Should return an array of custom error message objects.
   */
  errorMessage: PropTypes.func,

  /**
   * If true, displays validation errors below the input field when validation fails.
   * Default: true.
   */
  errors: PropTypes.bool,

  /**
   * Callback function that provides access to the underlying input element.
   * Useful for handling input focus or other direct manipulations.
   */
  setInputRef: PropTypes.func,
};
